import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import GenericInfoPanel from "../../../../../../components/Modals/GenericInfoPanel";
import {
  contactGeneralFields,
  contactAdditionalFields,
  initialContactFormData,
} from "../../config/buildingModalConfig";
import {
  deviceGeneralFields,
  deviceInspectionFields,
  defaultDeviceForm,
} from "../../config/buildingDeviceConfig";

import {
  updateContact,
  addNewContact,
} from "../../services/buildingContactService";
import {
  updateDevice,
  createNewDevice,
} from "../../services/buildingDeviceService";
import {
  selectedSelectedBuildingContacts,
  setSelectedBuildingContact,
  clearSelectedBuildingContact,
} from "../../../../../../redux/buildings/selectedBuildingContactSlice";
import {
  updateAssignedContact,
  addAssignedContact,
} from "../../../../../../redux/buildings/buildingContactsSlice";
import {
  selectSelectedBuildingDevices,
  setSelectedBuildingDevices,
  clearSelectedBuildingDevices,
} from "../../../../../../redux/buildings/selectedBuildingDevicesSlice";
import {
  updateDeviceInState,
  addDeviceToState,
} from "../../../../../../redux/buildings/buildingDevicesSlice";

const BuildingInformationDisplay = ({
  isEditMode,
  newMode,
  newEntityType,
  onCloseNewMode,
  updateList,
}) => {
  const dispatch = useDispatch();
  const selectedContact = useSelector(selectedSelectedBuildingContacts);
  const selectedDevice = useSelector(selectSelectedBuildingDevices);

  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (newMode) {
      if (newEntityType === "contact") {
        setFormData(initialContactFormData);
        dispatch(clearSelectedBuildingContact());
      } else if (newEntityType === "device") {
        setFormData(defaultDeviceForm);
        dispatch(clearSelectedBuildingDevices());
      }
    } else {
      if (selectedContact && selectedContact.contactName !== undefined) {
        setFormData(selectedContact);
      } else if (selectedDevice && selectedDevice.deviceId !== undefined) {
        setFormData(selectedDevice);
      } else {
        setFormData(null);
      }
    }
  }, [newMode, newEntityType, dispatch, selectedContact, selectedDevice]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (!newMode) {
      if (selectedContact && selectedContact.contactName !== undefined) {
        const updated = { ...selectedContact, [name]: value };
        dispatch(setSelectedBuildingContact(updated));
      } else if (selectedDevice && selectedDevice.deviceId !== undefined) {
        const updated = { ...selectedDevice, [name]: value };
        dispatch(setSelectedBuildingDevices(updated));
      }
    }
  };

  let entityType = null;
  if (newMode) {
    entityType = newEntityType;
  } else if (selectedContact && selectedContact.contactName !== undefined) {
    entityType = "contact";
  } else if (selectedDevice && selectedDevice.deviceId !== undefined) {
    entityType = "device";
  }

  if (!formData || !entityType) {
    return (
      <div className="text-center text-gray-500">
        <p>No contact or device selected.</p>
      </div>
    );
  }

  let title = "";
  let fieldSections = [];
  if (entityType === "contact") {
    title = newMode ? "New Contact" : `Contact - ${formData.contactName}`;
    fieldSections = [
      { label: "General", fields: contactGeneralFields },
      { label: "Additional Info", fields: contactAdditionalFields },
    ];
  } else if (entityType === "device") {
    title = newMode ? "New Device" : `Device - ${formData.deviceId}`;
    fieldSections = [
      { label: "General", fields: deviceGeneralFields },
      { label: "Inspection", fields: deviceInspectionFields },
    ];
  }

  const handleSubmit = async () => {
    try {
      let updatedEntity;
      if (newMode) {
        // Creating a new entity.
        if (entityType === "contact") {
          updatedEntity = await addNewContact(formData);
          dispatch(addAssignedContact(updatedEntity));
        } else if (entityType === "device") {
          updatedEntity = await createNewDevice(formData);
          dispatch(addDeviceToState(updatedEntity));
        }
      } else {
        // Updating an existing entity.
        if (entityType === "contact") {
          updatedEntity = await updateContact(formData.contactID, formData);
          dispatch(
            updateAssignedContact({
              contactID: formData.contactID,
              updates: updatedEntity,
            })
          );
          dispatch(setSelectedBuildingContact(updatedEntity));
        } else if (entityType === "device") {
          updatedEntity = await updateDevice(formData.deviceId, formData);
          dispatch(updateDeviceInState(updatedEntity));
          dispatch(setSelectedBuildingDevices(updatedEntity));
        }
      }
      // Refresh the list and close the new/edit mode.
      updateList();
      onCloseNewMode();
    } catch (error) {
      console.error("Submit failed:", error);
      alert(`Submit failed: ${error.message}`);
    }
  };

  return (
    <GenericInfoPanel
      title={title}
      fieldSections={fieldSections}
      formData={formData}
      isEditMode={isEditMode}
      newMode={newMode}
      onChange={handleChange}
      onSubmit={handleSubmit}
      onCancel={onCloseNewMode}
    />
  );
};

export default BuildingInformationDisplay;
